import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  getAutomaticLicenseStatistics,
  IAutomaticLicenseStatistics,
  IAutomaticLicenseTypeStatistics
} from '../../../Services/License';
import { asyncErrorHandler } from '@abstract/abstractwebcommon-shared/utils/AsyncErrorHandler';
import { handleError } from '@abstract/abstractwebcommon-client/ErrorHandler/ErrorHandler';
import './AutomaticLicenseStatistics.css';

/** Range options exposed in the dropdown. `value` is what we send to the API. */
const RANGE_OPTIONS: { labelKey: string; value: number | 'all' }[] = [
  { labelKey: 'admin.page.dashboard.automaticLicense.range.7d', value: 7 },
  { labelKey: 'admin.page.dashboard.automaticLicense.range.30d', value: 30 },
  { labelKey: 'admin.page.dashboard.automaticLicense.range.90d', value: 90 },
  { labelKey: 'admin.page.dashboard.automaticLicense.range.365d', value: 365 },
  { labelKey: 'admin.page.dashboard.automaticLicense.range.all', value: 'all' }
];

/** Format a bucket's `date` (start-of-period) for display under each bar. */
const formatBucketTick = (
  bucketing: 'day' | 'week' | 'month',
  isoDate: string
): string => {
  const date: Date = parseISO(isoDate);
  if (bucketing === 'day') {
    return format(date, 'd');
  }
  if (bucketing === 'week') {
    return format(date, 'MMM d');
  }
  return format(date, 'MMM yy');
};

/** Tooltip header label for a bar. */
const formatBucketTooltip = (
  bucketing: 'day' | 'week' | 'month',
  isoDate: string
): string => {
  const date: Date = parseISO(isoDate);
  if (bucketing === 'day') {
    return format(date, 'MMM d');
  }
  if (bucketing === 'week') {
    return `Week of ${format(date, 'MMM d, yyyy')}`;
  }
  return format(date, 'MMM yyyy');
};

const Chart = ({
  bucketing,
  series,
  emptyLabel
}: {
  bucketing: 'day' | 'week' | 'month';
  series: { date: string; count: number }[];
  emptyLabel: string;
}): JSX.Element => {
  if (!series.length) {
    return <div className="automatic-license-chart-empty">{emptyLabel}</div>;
  }
  const max: number = Math.max(...series.map((day) => day.count), 1);
  return (
    <div className="automatic-license-chart">
      {series.map((bucket) => (
        <div className="automatic-license-chart-day" key={bucket.date}>
          <div className="automatic-license-chart-bar-column">
            <div
              className="automatic-license-chart-bar"
              style={{
                // Always keep a tiny nub on zero-count buckets so the axis
                // reads cleanly.
                height: `${Math.max((bucket.count / max) * 100, bucket.count ? 8 : 2)}%`
              }}
            >
              <div className="automatic-license-chart-tooltip">
                <strong>{bucket.count}</strong>
                <small>{formatBucketTooltip(bucketing, bucket.date)}</small>
              </div>
            </div>
          </div>
          <span>{formatBucketTick(bucketing, bucket.date)}</span>
        </div>
      ))}
    </div>
  );
};

const StatCard = ({
  title,
  statistics,
  notConfiguredLabel,
  inRangeLabel
}: {
  title: string;
  statistics: IAutomaticLicenseTypeStatistics;
  notConfiguredLabel: string;
  inRangeLabel: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Card className="automatic-license-card mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-baseline mb-3">
          <h5 className="mb-0">{title}</h5>
          {statistics.projectName && (
            <small className="text-muted">{statistics.projectName}</small>
          )}
        </div>
        {!statistics.configured && statistics.stats.total === 0 ? (
          <div className="text-muted">{notConfiguredLabel}</div>
        ) : (
          <>
            <Row className="automatic-license-stats">
              <Col xs={4}>
                <div className="automatic-license-stat-value">
                  {statistics.stats.total}
                </div>
                <div className="automatic-license-stat-label">
                  {t('admin.page.dashboard.automaticLicense.total')}
                </div>
              </Col>
              <Col xs={4}>
                <div className="automatic-license-stat-value">
                  {statistics.stats.active}
                </div>
                <div className="automatic-license-stat-label">
                  {t('admin.page.dashboard.automaticLicense.active')}
                </div>
              </Col>
              <Col xs={4}>
                <div className="automatic-license-stat-value">
                  {statistics.stats.inRange}
                </div>
                <div className="automatic-license-stat-label">{inRangeLabel}</div>
              </Col>
            </Row>
            <Chart
              bucketing={statistics.stats.bucketing}
              series={statistics.stats.series}
              emptyLabel={t('admin.page.dashboard.automaticLicense.noData')}
            />
          </>
        )}
      </Card.Body>
    </Card>
  );
};

const AutomaticLicenseStatistics = (): JSX.Element | null => {
  const { t } = useTranslation();
  const [data, setData] = useState<IAutomaticLicenseStatistics | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rangeDays, setRangeDays] = useState<number | 'all'>(30);

  useEffect(() => {
    let isCancelled = false;
    const fetch = async () => {
      setIsLoading(true);
      try {
        const response = await asyncErrorHandler(
          getAutomaticLicenseStatistics(rangeDays)
        );
        if (isCancelled) {
          return;
        }
        if (response?.error) {
          handleError({ message: response.error.message });
          return;
        }
        setData(response?.data ?? null);
      } catch (error: any) {
        if (!isCancelled) {
          handleError({ message: error?.message });
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };
    fetch();
    return () => {
      isCancelled = true;
    };
  }, [rangeDays]);

  if (isLoading && !data) {
    return null;
  }
  if (!data) {
    return null;
  }

  const notConfigured: string = t(
    'admin.page.dashboard.automaticLicense.notConfigured'
  );
  // "In selected range" / "All-time" — label changes meaning depending on the
  // current selection so the counter card stays readable.
  const inRangeLabel: string =
    rangeDays === 'all'
      ? t('admin.page.dashboard.automaticLicense.allTimeCreated')
      : t('admin.page.dashboard.automaticLicense.inRange');

  return (
    <div className="automatic-license-statistics mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h4 className="mb-0">{t('admin.page.dashboard.automaticLicense.title')}</h4>
        <Form.Group className="automatic-license-range mb-0">
          <Form.Label className="mr-2 mb-0">
            {t('admin.page.dashboard.automaticLicense.range.label')}
          </Form.Label>
          <Form.Control
            as="select"
            size="sm"
            value={String(rangeDays)}
            onChange={(event) => {
              const next: string = event.target.value;
              setRangeDays(next === 'all' ? 'all' : Number(next));
            }}
          >
            {RANGE_OPTIONS.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>
                {t(option.labelKey)}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      </div>
      <Row>
        <Col md={12} lg={4}>
          <StatCard
            title={t('admin.page.dashboard.automaticLicense.pioneer')}
            statistics={data.pioneer}
            notConfiguredLabel={notConfigured}
            inRangeLabel={inRangeLabel}
          />
        </Col>
        <Col md={12} lg={4}>
          <StatCard
            title={t('admin.page.dashboard.automaticLicense.trial')}
            statistics={data.trial}
            notConfiguredLabel={notConfigured}
            inRangeLabel={inRangeLabel}
          />
        </Col>
        <Col md={12} lg={4}>
          <StatCard
            title={t('admin.page.dashboard.automaticLicense.paid')}
            statistics={data.paid}
            notConfiguredLabel={notConfigured}
            inRangeLabel={inRangeLabel}
          />
        </Col>
      </Row>
    </div>
  );
};

export default AutomaticLicenseStatistics;
